import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AnalysisApiClientAccess, AppRoleService } from '@nk/access-block';
import { ApplicationApiClient } from '@nk/api-client-lib';
import { DataSourceApiService } from '@nk/api-lib';
import { CachingService } from '@nk/caching-lib';
import { AppRoleKey, DataSourceCategoryEntity, DataSourceDetailsComposite } from '@nk/data-entities-lib';
import { map, Observable, switchMap } from 'rxjs';

@Injectable()
export class DataSourceService implements DataSourceApiService {
  constructor(
    private _applicationApi: ApplicationApiClient,
    private _analysisApiAccess: AnalysisApiClientAccess,
    private _cachingService: CachingService,
    private _appRoleService: AppRoleService,
  ) {}

  public dataSourceAvailableYearCache = this._cachingService.create(
    this,
    this.fetchDataSourceAvailableYear,
    true,
    3000,
  );

  public fetchDataSourceAvailableYear(
    dataSourceIdentifier: string,
    companyUuid?: string,
    shareholderCompanyUuid?: string,
  ): Observable<number[]> {
    let params = new HttpParams();
    if (dataSourceIdentifier) {
      params = params.append('data_source_identifier', dataSourceIdentifier);
    }
    if (companyUuid) {
      params = params.append('company_identifier', companyUuid); // Same as share issuer.
    }
    if (shareholderCompanyUuid) {
      params = params.append('shareholder_company_identifier', shareholderCompanyUuid);
    }
    return this._applicationApi
      .get<DataSourceDetailsComposite[]>(`/data-source-details-composite`, { params: params })
      .pipe(
        map((availableYearsList) => {
          if (availableYearsList.length > 1) {
            console.warn('Should only be one entry per datasourceKey');
          } else if (availableYearsList.length === 0) {
            console.warn(`Could not find data-source-details-composite about data source ${dataSourceIdentifier}`);
            return [];
          }
          const dataSourceAvailableYears = availableYearsList[0];
          const fromYear = dataSourceAvailableYears.data_source_details.from_date.getFullYear();
          const toYear = dataSourceAvailableYears.data_source_details.to_date.getFullYear();
          const years: number[] = [];
          for (let i = fromYear; i <= toYear; i++) {
            years.push(i);
          }
          return years;
        }),
      );
  }

  public dataSourceCategoryListCache = this._cachingService.create(this, this.fetchDataSourceCategoryList);
  public fetchDataSourceCategoryList(dataSourceDomainKey?: string): Observable<DataSourceCategoryEntity[]> {
    let httpParams = new HttpParams();
    if (dataSourceDomainKey != null) {
      httpParams = httpParams.append('data_source_domain_key', dataSourceDomainKey);
    }
    return this._appRoleService.claimValidAppRoleKeys([AppRoleKey.credit_check]).pipe(
      switchMap((validRoles) => {
        return this._analysisApiAccess.get<DataSourceCategoryEntity[]>(
          'data-source-category',
          {
            params: httpParams,
          },
          undefined,
          validRoles,
        );
      }),
    );
  }
}
